import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { PlanningDetailsResponse, PrescriptionPlanningStatus } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';

import { AppointmentTypes } from '@lib/core/models/visit.interface';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { PrescriptionIconComponent } from '../../commons/icon/prescription-icon.component';
import { AppointmentStatusControlComponent } from '../../controls/appointment-status-control/appointment-status-control.component';

interface AppointmentDetailsControls {
  serviceName: FormControl<string>;
  type: FormControl<AppointmentTypes>;
  status: FormControl<PrescriptionPlanningStatus>;
  statusReason: FormControl<string>;
  doctorName: FormControl<string | null>;
  date: FormControl<Date>;
  time: FormControl<string>;
  partnerName: FormControl<string>;
  locationName: FormControl<string>;
  address: FormControl<string>;
  city: FormControl<string>;
}

@Component({
  selector: 'prescriptions-appointment-details-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    TranslatePipe,
    AppointmentStatusControlComponent,
    LoaderComponent,
    PrescriptionIconComponent,
  ],
  templateUrl: './appointment-details-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentDetailsFormComponent {
  planningDetails = input<PlanningDetailsResponse | null>(null);

  readonly isChanged = signal(false);

  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  protected detailsForm: FormGroup<AppointmentDetailsControls> | null = null;

  get currentStatus(): PrescriptionPlanningStatus | null {
    return this.detailsForm?.controls.status.value ?? null;
  }

  constructor() {
    effect(() => {
      const details = this.planningDetails();
      if (!details) return;

      if (!this.detailsForm) {
        this.buildForm(details);
      }

      this.isChanged.set(false);
    });
  }

  reset(): void {
    const details = this.planningDetails();
    if (!details || !this.detailsForm) return;

    this.detailsForm.controls.status.setValue(details.status, { emitEvent: false });
    this.isChanged.set(false);
  }

  private buildForm(details: PlanningDetailsResponse): void {
    const appointmentDate = details.appointmentTime ? new Date(details.appointmentTime) : null;
    const timeString = appointmentDate ? `${appointmentDate.getHours().toString().padStart(2, '0')}:${appointmentDate.getMinutes().toString().padStart(2, '0')}` : null;

    this.detailsForm = this.fb.group<AppointmentDetailsControls>({
      serviceName: this.fb.control<string>(details.serviceName, [Validators.required]),
      type: this.fb.control<AppointmentTypes>(AppointmentTypes[details.serviceType as keyof typeof AppointmentTypes], [Validators.required]),
      status: this.fb.control<PrescriptionPlanningStatus>(details.status, [Validators.required]),
      statusReason: this.fb.control<string>(details.statusReason),
      doctorName: this.fb.control<string>(details.doctorName),
      date: this.fb.control<Date>(appointmentDate, [Validators.required]),
      time: this.fb.control<string>(timeString, [Validators.required]),
      partnerName: this.fb.control<string>(details.partnerName, [Validators.required]),
      locationName: this.fb.control<string>(details.locationName, [Validators.required]),
      address: this.fb.control<string>(details.locationAddress),
      city: this.fb.control<string>(details.locationCity),
    });

    this.detailsForm.controls.status.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      this.isChanged.set(value !== this.planningDetails()?.status);
    });
  }
}
